const VARIABLE_TYPE_LABELS: Record<string, string> = {
  model: "Model",
  weather: "Weather",
  threat: "Threat",
};

const VARIABLE_TYPE_ORDER = ["model", "weather", "threat"] as const;

/**
 * VariableSelectionDropdown Component
 *
 * A checkbox-based column selector grouped by variable type (model, weather, threat)
 * or by units when variableType is not available.
 * Used in MapTableInset for selecting which variables/columns to display.
 */

import { useState, useMemo, useRef, useEffect, useCallback } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, ChevronUp } from "lucide-react";

// Variable interface for the dropdown (id = variable_id for cross-model uniqueness)
export interface VariableOption {
  id: string;
  slug: string;
  name: string;
  units?: string | null;
  order?: number;
  variableType?: string;
}

interface VariableSelectionDropdownProps {
  availableVariables: VariableOption[];
  selectedVariables: string[];
  onSelectedVariablesChange: (variables: string[]) => void;
  formatVariableName?: (slug: string) => string;
  /** Button label (e.g. "Columns" or "Variables") */
  label?: string;
  /** Optional default variable ids/slugs; when provided, shows a "Default" button */
  defaultVariables?: string[];
}

/**
 * VariableSelectionDropdown - Checkbox-based column selector grouped by units
 * Matches the original Next.js implementation styling
 */
export function VariableSelectionDropdown({
  availableVariables,
  selectedVariables,
  onSelectedVariablesChange,
  formatVariableName,
  label = "Columns",
  defaultVariables,
}: VariableSelectionDropdownProps) {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  const [computedDropdownWidth, setComputedDropdownWidth] = useState(320);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    () => new Set(VARIABLE_TYPE_ORDER),
  );
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleSection = useCallback((key: string) => {
    setExpandedSections((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const useVariableTypeGrouping = availableVariables.some(
    (v) =>
      v.variableType &&
      ["model", "weather", "threat"].includes(
        String(v.variableType).toLowerCase(),
      ),
  );

  // Update dropdown position when opening
  const handleToggleDropdown = () => {
    if (!isDropdownOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const desiredWidth = useVariableTypeGrouping ? 480 : 320;
      const safeWidth = Math.min(desiredWidth, viewportWidth - 16);

      let left = rect.left + window.scrollX;
      if (left + safeWidth > viewportWidth) {
        left = viewportWidth - safeWidth - 8;
      }

      setDropdownPosition({
        top: rect.bottom + window.scrollY + 4,
        left: Math.max(8, left),
      });
      setComputedDropdownWidth(safeWidth);
    }
    setIsDropdownOpen(!isDropdownOpen);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        buttonRef.current &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        const dropdownElement = document.getElementById(
          "variable-dropdown-portal",
        );
        if (
          !dropdownElement ||
          !dropdownElement.contains(event.target as Node)
        ) {
          setIsDropdownOpen(false);
        }
      }
    };

    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () =>
        document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isDropdownOpen]);

  // Group by variable_type (model, weather, threat) when available, else by units
  const variablesBySection = useMemo(() => {
    const groups: Record<
      string,
      Array<{ id: string; slug: string; name: string; units: string | null; order: number }>
    > = {};

    availableVariables.forEach((variable) => {
      const sectionKey = useVariableTypeGrouping
        ? variable.variableType || "model"
        : variable?.units || "No Units";

      if (!groups[sectionKey]) {
        groups[sectionKey] = [];
      }

      groups[sectionKey].push({
        id: variable.id,
        slug: variable.slug,
        name:
          variable?.name ||
          (formatVariableName
            ? formatVariableName(variable.slug)
            : variable.slug),
        units: variable?.units || null,
        order: variable?.order || 999,
      });
    });

    return groups;
  }, [availableVariables, formatVariableName, useVariableTypeGrouping]);

  const handleSelectAll = () => {
    onSelectedVariablesChange(availableVariables.map((v) => v.id));
  };

  const handleClear = () => {
    onSelectedVariablesChange([]);
  };

  const handleSelectDefault = () => {
    if (!defaultVariables?.length) return;
    const validDefaults = defaultVariables
      .filter((v) => availableVariables.some((av) => av.id === v || av.slug === v))
      .map((v) => {
        const av = availableVariables.find((av) => av.id === v || av.slug === v);
        return av?.id ?? v;
      });
    if (validDefaults.length > 0) {
      onSelectedVariablesChange(validDefaults);
    }
  };

  const getSectionTitle = (sectionKey: string) =>
    useVariableTypeGrouping
      ? VARIABLE_TYPE_LABELS[sectionKey] || sectionKey
      : sectionKey === "No Units"
        ? "No Units"
        : sectionKey;

  const orderedSectionKeys = useVariableTypeGrouping
    ? [
        ...VARIABLE_TYPE_ORDER.filter((k) => variablesBySection[k]?.length),
        ...Object.keys(variablesBySection).filter(
          (k) => !(VARIABLE_TYPE_ORDER as readonly string[]).includes(k),
        ),
      ]
    : Object.keys(variablesBySection);

  const handleVariableToggle = (variableId: string, checked: boolean) => {
    if (checked) {
      onSelectedVariablesChange([...selectedVariables, variableId]);
    } else {
      onSelectedVariablesChange(
        selectedVariables.filter((v) => v !== variableId),
      );
    }
  };

  if (availableVariables.length === 0) return null;

  return (
    <div className="relative">
      {/* Dropdown Trigger */}
      <button
        ref={buttonRef}
        onClick={handleToggleDropdown}
        className="flex items-center gap-1.5 px-2 py-1.5 border rounded-md transition-all text-xs h-7 border-gray-200 bg-gray-50 hover:bg-gray-100 text-gray-700"
      >
        <span className="text-xs font-medium whitespace-nowrap">{label}</span>
        <span className="text-xs px-1 py-0.5 rounded bg-gray-200 text-gray-600">
          {selectedVariables.length}
        </span>
        {isDropdownOpen ? (
          <ChevronUp className="h-3 w-3" />
        ) : (
          <ChevronDown className="h-3 w-3" />
        )}
      </button>

      {/* Dropdown Content - Rendered in Portal */}
      {isDropdownOpen &&
        typeof document !== "undefined" &&
        createPortal(
          <div
            id="variable-dropdown-portal"
            className="fixed z-[9999] rounded-md border border-gray-200 bg-white p-2 shadow-xl"
            style={{
              top: dropdownPosition.top,
              left: dropdownPosition.left,
              width: computedDropdownWidth,
            }}
          >
            {/* Quick Actions */}
            <div className="flex gap-1 flex-wrap">
              <button
                onClick={handleSelectAll}
                className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                All
              </button>
              <button
                onClick={handleClear}
                className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
              >
                Clear
              </button>
              {defaultVariables && defaultVariables.length > 0 && (
                <button
                  onClick={handleSelectDefault}
                  className="px-2 py-1 text-xs rounded transition-colors bg-gray-100 hover:bg-gray-200 text-gray-700"
                >
                  Default
                </button>
              )}
            </div>

            {/* Variables: vertically split by type (model | weather | threat) or stacked by units */}
            <div
              className={
                useVariableTypeGrouping
                  ? "grid grid-cols-1 gap-0 sm:grid-cols-3 sm:gap-3"
                  : "max-h-48 space-y-2 overflow-y-auto"
              }
            >
              {orderedSectionKeys.map((sectionKey) => {
                const variables = variablesBySection[sectionKey] || [];
                const isExpanded = expandedSections.has(sectionKey);

                return (
                  <div
                    key={sectionKey}
                    className={
                      useVariableTypeGrouping
                        ? "flex min-w-0 flex-col border-b border-gray-200 last:border-b-0 sm:border-b-0 sm:border-r sm:pr-2 sm:last:border-r-0 sm:last:pr-0"
                        : "space-y-1"
                    }
                  >
                    {/* Section Header — collapsible on mobile */}
                    <button
                      type="button"
                      onClick={() => toggleSection(sectionKey)}
                      className="flex w-full items-center justify-between rounded px-1.5 py-1 text-xs bg-gray-100 flex-shrink-0 mb-1 sm:cursor-default"
                    >
                      <div className="flex items-center space-x-1 min-w-0">
                        <span className="font-semibold uppercase tracking-wide text-gray-600 truncate">
                          {getSectionTitle(sectionKey)}
                        </span>
                        <span className="px-1 py-0.5 rounded bg-gray-200 text-gray-600 shrink-0">
                          ({variables.length})
                        </span>
                      </div>
                      {useVariableTypeGrouping && (
                        <ChevronDown
                          className={`h-3 w-3 shrink-0 text-gray-500 transition-transform sm:hidden ${isExpanded ? "rotate-180" : ""}`}
                        />
                      )}
                    </button>

                    {/* Variables in this section */}
                    <div
                      className={`grid grid-cols-1 gap-0.5 overflow-y-auto pl-1 sm:block sm:max-h-56 sm:min-h-0 ${
                        useVariableTypeGrouping
                          ? isExpanded
                            ? "block max-h-40 min-h-0"
                            : "hidden"
                          : ""
                      }`}
                    >
                      {variables.map((variable) => (
                        <label
                          key={variable.id}
                          className="flex items-center space-x-1.5 p-1 rounded cursor-pointer transition-colors hover:bg-gray-50 text-gray-700"
                        >
                          <input
                            type="checkbox"
                            checked={selectedVariables.includes(variable.id)}
                            onChange={(e) =>
                              handleVariableToggle(
                                variable.id,
                                e.target.checked,
                              )
                            }
                            className="rounded border-gray-300 text-gray-600 focus:ring-gray-400 text-xs shrink-0"
                          />
                          <span className="text-xs font-medium flex-1 break-words">
                            {variable.name}
                          </span>
                          {variable.units && !useVariableTypeGrouping && (
                            <span className="text-xs px-1 py-0.5 rounded bg-gray-200 text-gray-500 shrink-0">
                              {variable.units}
                            </span>
                          )}
                        </label>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}

export default VariableSelectionDropdown;
